import React from 'react';

import { FormContext } from '../contexts';
import { ErrorMessageType, FormMode } from '../enums';
import { FieldProps } from '../interfaces';
import { isNullOrEmpty } from '../validations';

export const useValidateInput = (
  props: FieldProps,
  getError: (value: unknown) => string[] | null = () => null,
  checkIsNullOrEmpty: (value: unknown) => boolean = isNullOrEmpty
): {
  error: string | null;
  validateInput: (value: unknown) => void;
} => {
  const [errorMessage, setErrorMessage] = React.useState<string[] | null>(null);
  const { formErrorsDispatch, formErrors, formMode } = React.useContext(FormContext);

  const setNewError = (newError: string[] | null): void => {
    setErrorMessage(newError && newError.length > 0 ? newError : null);

    if (props.name && formErrorsDispatch) {
      formErrorsDispatch({
        type: 'Add',
        inputName: props.name,
        payload: {
          errors: newError ? newError : [],
          id: props.id,
          label: props.label ?? props.name ?? ''
        }
      });
    }
  };

  const findError = (): string | null => {
    const error = formErrors && props.name ? formErrors[props.name] : null;
    if (error?.errors) return error.errors.join(' / ');
    return null;
  };

  const getFieldErrors = (value: unknown): string[] | null => {
    if (checkIsNullOrEmpty(value)) {
      const errorMessages = props.customFieldProps?.errorMessages;
      if (props.required) {
        return [errorMessages?.required ?? `is ${ErrorMessageType.Required}`];
      }
      if (props.requiredToSubmit && formMode === FormMode.MarketSubmit) {
        return [errorMessages?.requiredToSubmit ?? `is ${ErrorMessageType.RequiredToSubmit}`];
      }
      return null;
    }
    return getError(value);
  };

  const validateInput = (value: unknown): void => {
    const error = getFieldErrors(value) ?? [];
    const customValidations = props.customFieldProps?.validations;
    const customErrors =
      error.length === 0 && customValidations
        ? customValidations
            .map((method) => method(value))
            .filter((x) => !x.isValid)
            .map((x) => x.errorMessage ?? 'Error validating field')
        : [];
    setNewError([...error, ...customErrors]);
  };

  return {
    error: errorMessage ? errorMessage.join(' / ') : findError(),
    validateInput
  };
};
